import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightToBracket, faChevronDown, faRoute, faCode, faShieldHalved, faSliders, faSatelliteDish, faBars, faXmark, faFlask, faChartLine, faCalculator, faRulerCombined, faUser } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const featureLinks: { to: string; label: string; desc: string; icon: IconDefinition }[] = [
    { to: '/features/research-pipeline', label: 'Research Pipeline', desc: 'Full strategy lifecycle overview', icon: faRoute },
    { to: '/features/workspace', label: 'Research Workspace', desc: 'Strategy IDE and code editor', icon: faCode },
    { to: '/features/optimization', label: 'Parameter Optimization', desc: 'Stability-first parameter search', icon: faSliders },
    { to: '/features/validation', label: 'Structured Validation', desc: 'Cross-asset and walk-forward testing', icon: faShieldHalved },
    { to: '/features/continuous-validation', label: 'Continuous Validation', desc: 'Adaptive Flow monitoring', icon: faSatelliteDish },
];

const labLinks: { to: string; label: string; desc: string; icon: IconDefinition }[] = [
    { to: '/labs/tradingview-indicator-tdr', label: 'TDR Indicator', desc: 'Free TradingView trend detection indicator', icon: faChartLine },
    { to: '/labs/strategy-performance-calculator', label: 'Strategy Calculator', desc: 'Expectancy, Kelly & Monte Carlo analysis', icon: faCalculator },
    { to: '/labs/position-size-calculator', label: 'Position Size Calculator', desc: 'Optimal sizing from risk & stop-loss', icon: faRulerCombined },
];

const PublicNavBar: React.FC = () => {
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const [labsOpen, setLabsOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isLoggedIn = !!localStorage.getItem('authToken');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const labsDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                !mobileMenuOpen
            ) {
                setFeaturesOpen(false);
            }
            if (
                labsDropdownRef.current &&
                !labsDropdownRef.current.contains(e.target as Node) &&
                !mobileMenuOpen
            ) {
                setLabsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuOpen]);

    // Close mobile menu on route change
    const closeMobile = () => {
        setMobileMenuOpen(false);
        setFeaturesOpen(false);
        setLabsOpen(false);
    };

    return (
        <nav className="fixed top-0 w-full bg-gray-900/80 backdrop-blur-md border-b border-gray-800 z-50 text-sm font-[Poppins]">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10">
                <div className="flex justify-between items-center h-16">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/quanthop_logo_dark.svg"
                            alt="Quanthop"
                            className="h-8 w-auto"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <div className="hidden md:flex items-center space-x-4">
                        {/* Features dropdown */}
                        <div ref={dropdownRef} className="relative">
                            <button
                                onClick={() => setFeaturesOpen(o => !o)}
                                className="flex items-center gap-1.5 px-4 py-2 text-gray-300 hover:text-white transition-colors"
                            >
                                Features
                                <FontAwesomeIcon icon={faChevronDown} className={`text-[9px] transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {featuresOpen && (
                                <div className="absolute top-full right-0 mt-1 w-72 bg-gray-900 border border-gray-700/60 rounded-lg shadow-xl shadow-black/40 overflow-hidden">
                                    {featureLinks.map((link, i) => (
                                        <Link
                                            key={link.to}
                                            to={link.to}
                                            onClick={() => setFeaturesOpen(false)}
                                            className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-800/60 transition-colors ${i > 0 ? 'border-t border-gray-800/40' : ''}`}
                                        >
                                            <FontAwesomeIcon icon={link.icon} className="text-white text-sm mt-0.5 w-4" />
                                            <div>
                                                <div className="text-sm text-gray-200 font-medium">{link.label}</div>
                                                <div className="text-[11px] text-gray-500 mt-0.5">{link.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Link
                            to="/research"
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Research
                        </Link>

                        {/* Labs dropdown */}
                        <div ref={labsDropdownRef} className="relative">
                            <button
                                onClick={() => setLabsOpen(o => !o)}
                                className="flex items-center gap-1.5 px-4 py-2 text-gray-300 hover:text-white transition-colors"
                            >
                                Labs
                                <FontAwesomeIcon icon={faChevronDown} className={`text-[9px] transition-transform ${labsOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {labsOpen && (
                                <div className="absolute top-full right-0 mt-1 w-72 bg-gray-900 border border-gray-700/60 rounded-lg shadow-xl shadow-black/40 overflow-hidden">
                                    {labLinks.map((link, i) => (
                                        <Link
                                            key={link.to}
                                            to={link.to}
                                            onClick={() => setLabsOpen(false)}
                                            className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-800/60 transition-colors ${i > 0 ? 'border-t border-gray-800/40' : ''}`}
                                        >
                                            <FontAwesomeIcon icon={link.icon} className="text-white text-sm mt-0.5 w-4" />
                                            <div>
                                                <div className="text-sm text-gray-200 font-medium">{link.label}</div>
                                                <div className="text-[11px] text-gray-500 mt-0.5">{link.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Link
                            to="/learn"
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Learn
                        </Link>
                        <Link
                            to="/docs"
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Documentation
                        </Link>
                        <Link
                            to="/pricing"
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Pricing
                        </Link>
                        <Link
                            to="/community"
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Community
                        </Link>
                        <Link
                            to={isLoggedIn ? '/app' : '/login'}
                            className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            <FontAwesomeIcon icon={isLoggedIn ? faUser : faArrowRightToBracket} className="text-sm" />
                            {isLoggedIn ? 'My Account' : 'Sign in'}
                        </Link>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setMobileMenuOpen(o => !o)}
                        className="md:hidden p-2 text-gray-300 hover:text-white transition-colors"
                        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                    >
                        <FontAwesomeIcon icon={mobileMenuOpen ? faXmark : faBars} className="text-xl" />
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="md:hidden border-t border-gray-800 bg-gray-900/95 backdrop-blur-md">
                    <div className="px-4 py-3 space-y-1">
                        {/* Features accordion */}
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setFeaturesOpen(o => !o)}
                            className="flex items-center justify-between w-full px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            <span>Features</span>
                            <FontAwesomeIcon icon={faChevronDown} className={`text-[9px] transition-transform ${featuresOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {featuresOpen && (
                            <div className="ml-3 border-l border-gray-700/50 pl-3 space-y-1">
                                {featureLinks.map(link => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        onClick={closeMobile}
                                        className="flex items-center gap-3 px-3 py-2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        <FontAwesomeIcon icon={link.icon} className="text-xs w-4" />
                                        <span className="text-sm">{link.label}</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        <Link
                            to="/research"
                            onClick={closeMobile}
                            className="block px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            Research
                        </Link>

                        {/* Labs accordion */}
                        <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setLabsOpen(o => !o)}
                            className="flex items-center justify-between w-full px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            <span>Labs</span>
                            <FontAwesomeIcon icon={faChevronDown} className={`text-[9px] transition-transform ${labsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {labsOpen && (
                            <div className="ml-3 border-l border-gray-700/50 pl-3 space-y-1">
                                {labLinks.map(link => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        onClick={closeMobile}
                                        className="flex items-center gap-3 px-3 py-2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        <FontAwesomeIcon icon={link.icon} className="text-xs w-4" />
                                        <span className="text-sm">{link.label}</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        <Link
                            to="/learn"
                            onClick={closeMobile}
                            className="block px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            Learn
                        </Link>
                        <Link
                            to="/docs"
                            onClick={closeMobile}
                            className="block px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            Documentation
                        </Link>
                        <Link
                            to="/pricing"
                            onClick={closeMobile}
                            className="block px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            Pricing
                        </Link>
                        <Link
                            to="/community"
                            onClick={closeMobile}
                            className="block px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                        >
                            Community
                        </Link>
                        <div className="pt-2 border-t border-gray-800/60">
                            <Link
                                to={isLoggedIn ? '/app' : '/login'}
                                onClick={closeMobile}
                                className="flex items-center gap-2 px-3 py-2.5 text-gray-300 hover:text-white transition-colors"
                            >
                                <FontAwesomeIcon icon={isLoggedIn ? faUser : faArrowRightToBracket} className="text-sm" />
                                {isLoggedIn ? 'My Account' : 'Sign in'}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default PublicNavBar;
